import { Maybe } from '@paradigm/utils/types';
import { formatProductPrice } from '#/unified-rfqs/ui/rfqs/rfqs-functions';
import { RfqType, Side, getOppositeSide } from '#/unified-rfqs/entities/shared';
import { findBaseProductCode } from '#/products/domain';

import { GrfqQuote } from '#/unified-rfqs/entities/grfq/domain';
import { isTaker as isDrfqTaker } from '@paradigm/features/src/unified-rfqs/entities/drfq/domain';
import { Drfq } from '#/unified-rfqs/entities/drfq/domain';
import {
  formatCustomNonIntegerPrice,
  isCustomNonIntegerRatio,
} from '#/unified-rfqs/utils/rfq-price-functions';

interface Args {
  price: Maybe<string>;
  productCode: Maybe<string>;
  side: Maybe<Side>;
  rfqType: Maybe<RfqType>;
  description: Maybe<string>;
}

export default function useFormattedPrice({ price, ...rest }: Args): string {
  const formatter = usePriceFormatterFn(rest);

  return formatter(price);
}

export function usePriceFormatterFn({
  productCode,
  side,
  rfqType,
  description,
}: Omit<Args, 'price'>) {
  const defaultFormater = (price: Maybe<string>) =>
    formatProductPrice(price ?? null, productCode ?? undefined);

  const isCustomNonInteger = isCustomNonIntegerRatio(description);
  if (!isCustomNonInteger || productCode == null) {
    return defaultFormater;
  }

  switch (rfqType) {
    case 'grfq':
    case 'drfq':
      return (price: Maybe<string>, customSide?: Maybe<Side>) =>
        formatCustomNonIntegerPrice(price, productCode, customSide ?? side);
    default:
      return defaultFormater;
  }
}

export function useFormattedGrfqQuotePrice(quote: Maybe<GrfqQuote>) {
  return useFormattedPrice({
    price: quote?.price,
    productCode: quote?.product_codes?.[0],
    side: quote?.side,
    rfqType: 'grfq',
    description: quote?.description,
  });
}

/**
 * Formats GRFQ price considering the role, always show the MAKER side price.
 */
export function useFormattedGrfqMakerPrice({
  price,
  side,
  productCode,
  description,
  isTaker,
}: Omit<Args, 'rfqType'> & { isTaker: Maybe<boolean> }) {
  const makerPriceSide =
    isTaker === true && side != null ? getOppositeSide(side) : side;

  return useFormattedPrice({
    price,
    productCode,
    side: makerPriceSide,
    rfqType: 'grfq',
    description,
  });
}

type DrfqInfo = Pick<Drfq, 'role' | 'product_codes' | 'description'>;
interface DrfqFormattedPrice {
  price: Maybe<string>;
  side: Maybe<Side>;
  drfqInfo: Maybe<DrfqInfo>;
}

/**
 * Formats executed price of drfq considering the user role.
 * We should always display the same price submitted by the MAKER,
 * so we invert the side if the user is a taker.
 */
export function useFormattedDrfqMakerPrice({
  price,
  side,
  drfqInfo,
}: DrfqFormattedPrice) {
  const { product_codes = [], description } = drfqInfo ?? {};

  const baseLegProductCode = findBaseProductCode(product_codes);
  const makerPriceSide =
    drfqInfo != null && side != null && isDrfqTaker(drfqInfo)
      ? getOppositeSide(side)
      : side;

  return useFormattedPrice({
    price,
    side: makerPriceSide,
    productCode: baseLegProductCode,
    rfqType: 'drfq',
    description,
  });
}
